import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { MessageCircle, X, Send } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

// WhatsApp number for direct chat
const WHATSAPP_NUMBER = "31620192358";

// Opens WhatsApp chat directly
export const openWhatsAppChat = () => {
  const message = encodeURIComponent("Hallo Nextconvert, ik heb een vraag over jullie diensten.");
  window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, "_blank");
};

// Legacy function - now redirects to WhatsApp
export const openLiveChat = () => {
  openWhatsAppChat();
};

const LiveChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [visitorName, setVisitorName] = useState("");
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSent, setIsSent] = useState(false);
  
  // Honeypot fields for bot protection (should remain empty)
  const [honeypot, setHoneypot] = useState("");
  const [honeypot2, setHoneypot2] = useState("");
  
  // Track form start time for timing-based bot detection
  const formStartTimeRef = useRef<number>(0);

  // Load saved name
  useEffect(() => {
    const savedName = localStorage.getItem("chat_visitor_name");
    if (savedName) {
      setVisitorName(savedName);
    }
  }, []);

  // Listen for custom event to open the chat
  useEffect(() => {
    const handleOpenChat = () => {
      setIsOpen(true);
    };
    
    window.addEventListener("open-live-chat", handleOpenChat);
    return () => {
      window.removeEventListener("open-live-chat", handleOpenChat);
    };
  }, []);
  
  // Set form start time when chat opens
  useEffect(() => {
    if (isOpen && !isSent) {
      formStartTimeRef.current = Date.now();
    }
  }, [isOpen, isSent]);

  const handleSendMessage = async () => {
    if (!visitorName.trim() || !message.trim()) return;
    
    // Don't send if honeypot fields are filled (bot detected)
    if (honeypot || honeypot2) {
      console.log("Bot detected via honeypot");
      setIsSent(true); // Pretend success to not tip off bots
      setMessage("");
      return;
    }
    
    setIsLoading(true);
    localStorage.setItem("chat_visitor_name", visitorName);

    try {
      await supabase.functions.invoke("notify-whatsapp", {
        body: { 
          visitorName: visitorName.trim(), 
          message: message.trim(),
          // Bot protection fields
          website: honeypot, // honeypot field
          phone_confirm: honeypot2, // second honeypot
          _formStartTime: formStartTimeRef.current // timing check
        },
      });
      setIsSent(true);
      setMessage("");
    } catch (err) {
      console.error("Error sending message:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (visitorName.trim() && message.trim()) {
        handleSendMessage();
      }
    }
  };

  const handleNewMessage = () => {
    setIsSent(false);
    formStartTimeRef.current = Date.now(); // Reset form start time
  };

  return (
    <>
      {/* Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-20 right-4 md:bottom-6 md:right-6 z-50 bg-[#01152f] hover:bg-[#0f2236] text-white rounded-full p-4 shadow-xl transition-all hover:scale-105"
          aria-label="Open chat"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-0 right-0 md:bottom-6 md:right-6 z-50 w-full md:w-96 h-auto md:max-h-[500px] bg-white rounded-t-2xl md:rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200">
          {/* Header */}
          <div className="bg-[#01152f] text-white p-4 flex items-center justify-between">
            <div>
              <h3 className="font-bold text-lg">Live <span className="accent-serif">Chat</span></h3>
              <p className="text-xs text-white/70">Meestal binnen 5 minuten reactie</p>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="hover:bg-white/20 rounded-full p-1 transition-colors"
              aria-label="Sluit chat"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 flex flex-col p-4 bg-gray-50">
            {isSent ? (
              <div className="text-center py-8 space-y-4">
                <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                  <Send className="w-8 h-8 text-green-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-800">Bericht verzonden!</p>
                  <p className="text-sm text-gray-600 mt-1">We reageren zo snel mogelijk via WhatsApp.</p>
                </div>
                <Button 
                  onClick={handleNewMessage}
                  variant="outline"
                  className="mt-4"
                >
                  Nog een bericht sturen
                </Button>
              </div>
            ) : (
              <>
                {/* Welcome message */}
                <div className="flex justify-start mb-4">
                  <div className="max-w-[85%] rounded-2xl px-4 py-3 bg-white text-gray-800 border border-gray-200 rounded-bl-md shadow-sm">
                    <p className="text-sm font-medium mb-1">👋 Hallo!</p>
                    <p className="text-sm">
                      Welkom bij Nextconvert. Vul hieronder je naam en vraag in, dan reageren we via WhatsApp.
                    </p>
                  </div>
                </div>

                {/* Form */}
                <div className="mt-auto space-y-3">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Je naam</label>
                    <Input
                      value={visitorName}
                      onChange={(e) => setVisitorName(e.target.value.slice(0, 100))}
                      placeholder="Bijv. Jan de Vries"
                      maxLength={100}
                      className="bg-white"
                    />
                  </div>
                  
                  {/* Honeypot fields - hidden from humans but bots will fill them */}
                  <div 
                    aria-hidden="true" 
                    className="sr-only"
                  >
                    <label htmlFor="chat-website">Website (leave empty)</label>
                    <input
                      type="text"
                      id="chat-website"
                      name="chat_website"
                      value={honeypot}
                      onChange={(e) => setHoneypot(e.target.value)}
                      tabIndex={-1}
                      autoComplete="off"
                    />
                    <label htmlFor="chat-phone-confirm">Phone Confirm (leave empty)</label>
                    <input
                      type="text"
                      id="chat-phone-confirm"
                      name="chat_phone_confirm"
                      value={honeypot2}
                      onChange={(e) => setHoneypot2(e.target.value)}
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Je vraag</label>
                    <Textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value.slice(0, 1000))}
                      onKeyPress={handleKeyPress}
                      placeholder="Waar kunnen we je mee helpen?"
                      maxLength={1000}
                      className="bg-white min-h-[80px] resize-none"
                      rows={3}
                    />
                  </div>
                  <Button 
                    onClick={handleSendMessage} 
                    disabled={!visitorName.trim() || !message.trim() || isLoading}
                    className="w-full bg-[#01152f] hover:bg-[#0f2236]"
                  >
                    {isLoading ? "Versturen..." : "Verstuur bericht"}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default LiveChat;